// AppCategory.js
import React, { useState, useEffect, useMemo } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { CircularProgress, Avatar, Box, TextField } from "@mui/material"; // Import TextField for search input
import WarningIcon from '@mui/icons-material/Warning';
import { red } from '@mui/material/colors';
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import MDButton from "components/MDButton";
import IconButton from "@mui/material/IconButton";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Swal from "sweetalert2";
import AppCategoryAddModel from "./app_category_add_model";
import AppCategoryEditModel from "./app_category_edit_model";
import api from "core/interceptors/api";
import { disableBackNavigation } from "core/utils/storage";
import { useNavigate } from "react-router-dom";
import LogoutButton from "layouts/logout";
import { ServerBaseUrlForStaticImages } from "constants";
import ImageWithLoader from './image_with_loader';

const BASE_IMAGE_URL = ServerBaseUrlForStaticImages;

export default function AppCategory() {
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [editInput, setEditInput] = useState({});
  const [loading, setLoading] = useState(true);
  const [appCategories, setAppCategories] = useState([]);
  const [deleteCategoryId, setDeleteCategoryId] = useState(null);
  const navigate = useNavigate();

  // State for pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // State for global search
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    disableBackNavigation();
    fetchAppCategories();
  }, []);

  const handleCloseAddModal = () => {
    setOpenAddModal(false);
    fetchAppCategories();
  };

  const handleClose = () => {
    setEditInput({});
    setOpenAddModal(false);
    setOpenEditModal(false);
    setOpenDeleteModal(false);
    fetchAppCategories();
  };

  const handleEditChange = (event) => {
    const { name, value } = event.target;
    setEditInput((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const fetchAppCategories = async () => {
    setLoading(true);
    try {
      const response = await api.get("/app_management/app_category/");
      if (response.data.success === 1) {
        setAppCategories(response.data.app_category);
        // Page adjustment will happen after filtering below
      } else {
        Swal.fire("Error", "Failed to load app categories. Please try again later.", "error");
      }
    } catch (error) {
      console.error("Error fetching app categories:", error);
      Swal.fire("Error", "Failed to load app categories. Please try again later.", "error");
    } finally {
      setLoading(false);
    }
  };

  const deleteAppCategory = async (event) => {
    event.preventDefault();
    try {
      handleClose();
      const response = await api.delete(`/app_management/app_category/${deleteCategoryId}`);
      if (response.data.status === 1) {
        Swal.fire({
          title: response.data.msg,
          icon: "success",
          timer: 1500,
        });
        setPage(0); // Reset page to 0 after deleting
        fetchAppCategories(); // Re-fetch to update the table
      } else {
        Swal.fire({
          title: "Error",
          text: response.data.msg,
          icon: "error",
        });
      }
    } catch (error) {
      console.error("Error deleting app category:", error);
      Swal.fire({
        title: "Error",
        text: "An unexpected error occurred",
        icon: "error",
      });
    }
  };

  // Pagination handlers
  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event, newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0); // Reset to first page when rows per page changes
  };

  // Filter categories based on search query
  const filteredCategories = useMemo(() => {
    if (!searchQuery) {
      return appCategories;
    }
    const lowerCaseQuery = searchQuery.toLowerCase();
    return appCategories.filter(category =>
      category.app_category_name.toLowerCase().includes(lowerCaseQuery)
    );
  }, [appCategories, searchQuery]);

  // Adjust page if current page is out of bounds after filtering
  useEffect(() => {
    const totalFilteredItems = filteredCategories.length;
    if (page * rowsPerPage >= totalFilteredItems && totalFilteredItems > 0) {
      setPage(0); // Go to first page if current page is now empty
    } else if (totalFilteredItems === 0 && page !== 0) {
      setPage(0); // If no items, ensure page is 0
    }
  }, [page, rowsPerPage, filteredCategories]);


  // Prepare data for DataTable with pagination on filtered data
  const startIndex = page * rowsPerPage;
  const paginatedCategories = Array.isArray(filteredCategories)
    ? filteredCategories.slice(startIndex, startIndex + rowsPerPage)
    : [];

  const columns = useMemo(() => [
    {
      Header: "S.No",
      accessor: "s_no",
      align: "center",
    },
    {
      Header: "Image",
      accessor: "image",
      align: "center",
      Cell: ({ row }) => (
        <ImageWithLoader
          imageUrl={row.original.image_url}
          altText={row.original.app_category_name}
        />
      ),
    },
    {
      Header: "App Category",
      accessor: "app_category_name",
      sortable: true,
      align: "center",
    },
    {
      Header: "Options",
      accessor: "options",
      align: "center",
      Cell: ({ row }) => (
        <div style={{ display: "flex", justifyContent: "center", gap: "10px" }}>
          <IconButton
            color="success"
            onClick={() => {
              setOpenEditModal(true);
              setEditInput(row.original);
            }}
          >
            <EditIcon />
          </IconButton>
          <IconButton
            color="error"
            onClick={() => {
              setDeleteCategoryId(row.original.app_category_id);
              setOpenDeleteModal(true);
            }}
          >
            <DeleteIcon />
          </IconButton>
        </div>
      ),
    },
  ], []);

  const data = paginatedCategories.map((cat, index) => ({
    s_no: startIndex + index + 1,
    app_category_id: cat.id,
    app_category_name: cat.app_category_name,
    image_url: cat.image_filename
      ? `${BASE_IMAGE_URL.replace(/\/$/, "")}/app_management/app_category/uploads/app_category/${cat.image_filename}`
      : null,
  }));

  return (
    <DashboardLayout>
      <MDBox display="flex" justifyContent="flex-end" alignItems="center">
        <LogoutButton onLogout={() => navigate("/login")} />
      </MDBox>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox mx={2} mt={-3} py={3} px={2} variant="gradient" bgColor="info" borderRadius="lg" coloredShadow="info">
                <MDTypography variant="h6" color="white">
                  App Categories
                </MDTypography>
              </MDBox>
              <MDBox display="flex" justifyContent="space-between" alignItems="center" mt={4} mr={4} ml={4}>
                {/* Global Search Input */}
                <TextField
                  label="Search App Categories"
                  variant="outlined"
                  size="small"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  sx={{ width: '300px' }} // Adjust width as needed
                />
                <MDButton color="success" onClick={() => setOpenAddModal(true)}>
                  Add App Category
                </MDButton>
              </MDBox>
              <MDBox p={3} display="flex" justifyContent="center" alignItems="center" minHeight="50svh">
                {loading ? (
                  <CircularProgress color="primary" />
                ) : (
                  <DataTable
                    table={{ columns: columns, rows: data }}
                    isSorted={true}
                    entriesPerPage={{ defaultValue: rowsPerPage, entries: [5, 10, 15, 20, 25] }}
                    showTotalEntries={true}
                    canSearch={false} // Disable DataTable's internal search as we're doing global search
                    noEndBorder
                    pagination={{
                      variant: "contained",
                      color: "dark",
                      page: page,
                      onPageChange: handlePageChange,
                      rowsPerPage: rowsPerPage,
                      onRowsPerPageChange: handleRowsPerPageChange,
                      count: filteredCategories.length, // Total count for pagination is now based on filtered data
                    }}
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <AppCategoryAddModel open={openAddModal} handleClose={handleCloseAddModal} />

      <AppCategoryEditModel
        open={openEditModal}
        handleClose={handleClose}
        editInput={editInput}
        handleEditChange={handleEditChange}
      />

      <Dialog open={openDeleteModal} onClose={handleClose}>
        <MDBox component="form" onSubmit={deleteAppCategory}>
          <DialogTitle>
            <Grid container spacing={2} alignItems="center">
              <Grid item>
                <WarningIcon style={{ color: red[700], fontSize: 40 }} />
              </Grid>
              <Grid item>
                <MDTypography variant="h6" component="span">
                  Confirm Delete
                </MDTypography>
              </Grid>
            </Grid>
          </DialogTitle>
          <DialogContent>
            <MDBox p={2} style={{ backgroundColor: red[50], borderLeft: `5px solid ${red[700]}` }}>
              <MDTypography variant="body1">
                Are you sure you want to delete this app category? This action cannot be undone.
              </MDTypography>
            </MDBox>
          </DialogContent>
          <DialogActions>
            <MDButton onClick={handleClose} style={{ color: "gray" }}>
              Cancel
            </MDButton>
            <MDButton type="submit" color="primary">
              Delete
            </MDButton>
          </DialogActions>
        </MDBox>
      </Dialog>
    </DashboardLayout>
  );
}